import { lazy, ComponentType } from 'react';

/**
 * A wrapper for React.lazy that detects failure to load a chunk (often due to new deployments)
 * and forces a full page reload to fetch the latest version of the app.
 *
 * El generico es el TIPO DEL COMPONENTE, no el de sus props — la misma firma que
 * declara `React.lazy`. Parametrizarlo por las props hacia que TypeScript
 * comparase la varianza de estas y rechazara los componentes sin props
 * (`React.FC` a secas), que son la mitad de las rutas del rider.
 *
 * El `any` de la restriccion es el de React: vive solo en la restriccion del
 * generico, no en ninguna llamada, asi que no debilita a quien lo usa.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRetry = <T extends ComponentType<any>>(
    componentImport: () => Promise<{ default: T }>
) =>
    // El retorno se declara explicitamente: sin anotarlo, TypeScript infiere la
    // union del import real y del componente vacio del reintento, y esa union no
    // encaja con lo que `lazy` espera.
    lazy<T>(async (): Promise<{ default: T }> => {
        // Check if we already tried reloading in the last 10 seconds to prevent loops
        const lastRetry = window.sessionStorage.getItem('last-chunk-retry');
        const now = Date.now();
        const isRecentlyRetried = lastRetry && (now - parseInt(lastRetry)) < 10000;

        try {
            return await componentImport();
        } catch (error) {
            console.error("[LAZY RETRY] Failed to load component:", error);

            if (!isRecentlyRetried) {
                window.sessionStorage.setItem('last-chunk-retry', now.toString());
                console.warn("[LAZY RETRY] Outdated chunk detected. Forcing app reload...");
                window.location.reload();
                // Componente vacio mientras la pagina se recarga.
                return { default: (() => null) as unknown as T };
            }

            // If it still fails after a reload, throw the error to the ErrorBoundary
            throw error;
        }
    });
